/**
 * Cashflow horizon (TMMAPP-0010, design §8.1 / §14.5): 3 · 6 · 12 months, today-forward.
 * `from` is omitted (server default = today); `to` is the app's today (the person's `/me` zone, not
 * the browser's) + N months by the one civil-month rule, so 12 months never passes tmm-api's
 * longest window (TMMAPP-0171 item 8).
 */
import { addCivilMonths } from '@/shared/servedWindow';
import { numberEnumParam } from '@/shared/useUrlState';

export const HORIZONS = [3, 6, 12] as const;
export type HorizonMonths = (typeof HORIZONS)[number];

/** The `h` param: 3 months (omitted from the URL), 6 or 12; anything else reads as 3. */
export const HORIZON_SCHEMA = { h: numberEnumParam(HORIZONS, 3) };

/** The query window for a horizon: omit `from`, set `to` to `today` (civil) + N months. */
export function horizonWindow(months: HorizonMonths, today: string): { to: string } {
  return { to: addCivilMonths(today, months) };
}

/** First and last civil day of the month that contains `civil`. */
export function monthBounds(civil: string): { from: string; to: string } {
  const year = Number(civil.slice(0, 4));
  const month = Number(civil.slice(5, 7));
  const last = new Date(year, month, 0).getDate();
  const mm = civil.slice(5, 7);
  return {
    from: `${year}-${mm}-01`,
    to: `${year}-${mm}-${String(last).padStart(2, '0')}`,
  };
}

/** Inclusive clamp of `inner` to `outer` on civil strings. */
export function clampWindow(
  inner: { from: string; to: string },
  outer: { from: string; to: string },
): { from: string; to: string } {
  return {
    from: inner.from < outer.from ? outer.from : inner.from,
    to: inner.to > outer.to ? outer.to : inner.to,
  };
}
